import { Trans } from '@lingui/react'
import {
  OfferConditionTypeEnumV2,
  OfferDiscountTypeEnumV2,
  type SellerPromotionV2,
} from '@redteclab/api/clients/bully'
import { type ReactNode } from 'react'

import { TENANT_AT, TENANT_BE, TENANT_COM, TENANT_IT } from 'shared/consts'
import { type PublicRuntimeConfig } from 'types/shopConfig'
import IconArrowRight from 'views/components/atoms/Icon/IconArrowRight'
import LinkWithIcon from 'views/components/molecules/LinkWithIcon'
import Notification from 'views/components/molecules/Notification'
import useMountPoints from 'views/enhancers/useMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'

interface messageSettingsProps {
  headline: ReactNode
  translation: ReactNode
  link: boolean
}

const getFormattedDiscountAmount = (sellerPromotion: SellerPromotionV2) => {
  switch (sellerPromotion.discountType) {
    case 'totalAmount': {
      const totalAmountValue = sellerPromotion.discountValue.toFixed(0)
      return `${totalAmountValue}€`
    }
    case 'totalPercent': {
      const percValue = sellerPromotion.discountValue * 100
      return `${percValue.toFixed(0)}%`
    }
    case 'freeItems':
      if (sellerPromotion.conditionType === 'minimumSameItemQuantity') {
        return `${
          sellerPromotion.conditionValue - sellerPromotion.discountValue
        }`
      }
      if (sellerPromotion.conditionType === 'minimumQuantity') {
        return `${sellerPromotion.discountValue}`
      }
      break
    default:
      return ''
  }
}

const getFormattedMovAmount = (sellerPromotion: SellerPromotionV2) => {
  if (sellerPromotion.minimumOrderValue) {
    return sellerPromotion.minimumOrderValue + '€'
  }
  if (sellerPromotion.conditionValue) {
    return sellerPromotion.conditionValue + '€'
  }
}

export const getLegalSupNote = (tenant: string) => {
  if (tenant.includes(TENANT_COM)) {
    return 13
  }
  if (tenant.includes(TENANT_AT)) {
    return 16
  }
  if (tenant.includes(TENANT_BE)) {
    return 3
  }
  if (tenant.includes(TENANT_IT)) {
    return 11
  }

  return 13
}

const GetSellerPromotionNotification = (props: {
  sellerPromotion: SellerPromotionV2
  sellerName: string
  sellerId: string
  publicRuntimeConfig: PublicRuntimeConfig
}): ReactNode => {
  const {
    sellerId,
    sellerName,
    sellerPromotion,
    publicRuntimeConfig: {
      publicConfig: { tenant },
    },
  } = props

  const mountPoints = useMountPoints()

  const messageSettings: messageSettingsProps = {
    headline: null,
    translation: null,
    link: false,
  }

  const isDiscountTypeTotalAmountOrPercent =
    sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalAmount ||
    sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalPercent

  const conditionValue = sellerPromotion?.conditionValue
  const discountValue = getFormattedDiscountAmount(sellerPromotion)

  const legalSup = getLegalSupNote(tenant)

  switch (sellerPromotion.conditionType) {
    case OfferConditionTypeEnumV2.MinimumSameItemQuantity:
      if (sellerPromotion.discountType === OfferDiscountTypeEnumV2.FreeItems) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumSameItemQuantity.freeItems"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumSameItemQuantity.freeItems"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
      } else if (
        sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalAmount
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumSameItemQuantity.total"
            values={{
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumSameItemQuantity.totalAmount"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
            }}
          />
        )
      } else if (
        sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalPercent
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumSameItemQuantity.total"
            values={{
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumSameItemQuantity.totalPercent"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
            }}
          />
        )
      }
      break
    case OfferConditionTypeEnumV2.MinimumQuantity:
      messageSettings.link = true
      if (sellerPromotion.discountType === OfferDiscountTypeEnumV2.FreeItems) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumQuantity.freeItem"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumQuantity.freeItems"
            values={{
              formattedConditionAmount: conditionValue,
              sellerName,
            }}
          />
        )
      } else if (
        sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalAmount
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumQuantity.total"
            values={{
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumQuantity.totalAmount"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sellerName,
            }}
          />
        )
      } else if (
        sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalPercent
      ) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.minimumQuantity.total"
            values={{
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.minimumQuantity.totalPercent"
            values={{
              formattedConditionAmount: conditionValue,
              formattedDiscountAmount: discountValue,
              sellerName,
            }}
          />
        )
      }
      break
    case OfferConditionTypeEnumV2.MinimumOrderValue:
      messageSettings.link = true

      if (isDiscountTypeTotalAmountOrPercent) {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title.mov.total"
            values={{
              formattedDiscountAmount: discountValue,
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message.mov.total"
            values={{
              formattedMovAmount: getFormattedMovAmount(sellerPromotion),
              sup: <sup>{legalSup}</sup>,
            }}
          />
        )
      } else {
        messageSettings.headline = (
          <Trans
            id="seller.promotion.sellerBanner.title"
            values={{
              formattedMovAmount: discountValue,
            }}
          />
        )
        messageSettings.translation = (
          <Trans
            id="seller.promotion.sellerBanner.message"
            values={{
              formattedMovAmount: getFormattedMovAmount(sellerPromotion),
            }}
          />
        )
      }
      break
    default:
      break
  }

  return (
    <Notification
      alignIcon="top"
      className="u-background-color--background"
      dataQaId={`seller-not-applied-promotion-banner-${sellerId}`}
      heading={messageSettings.headline}
      label={
        <div>
          <div
            className="u-margin-x-small--bottom"
            data-qa-id={`seller-not-applied-promotion-banner-message-${sellerId}`}
          >
            {messageSettings.translation}
          </div>
          {messageSettings.link &&
            (isDiscountTypeTotalAmountOrPercent ||
            sellerPromotion.discountType ===
              OfferDiscountTypeEnumV2.FreeItems ? (
              <LinkWithIcon
                className="o-Cart-Notification-link-marketplace-promotions"
                data-qa-id={`seller-not-applied-promotion-banner-serp-link-${sellerId}`}
                href={mountPoints.getSellerProducts(sellerId)}
                icon={<IconArrowRight />}
                right
              >
                <span className="u-font-weight--semi-bold">
                  <Trans id="seller.promotion.product.banner.sellerSerpLink" />
                </span>
              </LinkWithIcon>
            ) : (
              <a
                href={mountPoints.getSellerProducts(sellerId)}
                className="o-Cart-Notification-link"
                data-qa-id={`seller-not-applied-promotion-banner-serp-link-${sellerId}`}
              >
                <Trans id="seller.promotion.product.banner.sellerSerpLink" />
              </a>
            ))}
        </div>
      }
      type={'infoPromotion'}
    />
  )
}

export default withShopAndRuntimeConfig(GetSellerPromotionNotification)
